// All comments in English only
import { useMemo } from "react";
import { useQuery, useQueries } from "@tanstack/react-query";
import {
  getCustomerSummary,
  getCustomerProducts,
  getCompanyRiskHistory,
  getProductTrend,
  getProductRiskTrend,
  getProductLLMAnalysis,
  type CustomerSummaryResponse,
  type ProductsListResponse,
  type ProductTrendResponse,
  type ProductRiskTrendResponse,
  type ProductLLMAnalysisResponse,
  type CompanyRiskHistoryResponse,
} from "../api/customerDetail";

type QResult<T> = { data?: T; isLoading: boolean; error: unknown };

export function useCustomerDetail(
  company: string,
  opts?: { months?: number; as_of_month?: string }
) {
  const months = opts?.months ?? 12;
  const as_of_month = opts?.as_of_month;

  // Customer-level summary (top risk, high-level metrics)
  const summaryQ = useQuery<CustomerSummaryResponse>({
    queryKey: ["customer-summary", company, as_of_month],
    queryFn: async () => (await getCustomerSummary(company, as_of_month)).data,
    refetchOnWindowFocus: false,
  }) as QResult<CustomerSummaryResponse>;

  // Products list for the customer
  const productsQ = useQuery<ProductsListResponse>({
    queryKey: ["customer-products", company, as_of_month],
    queryFn: async () =>
      (await getCustomerProducts(company, as_of_month, 1, 50)).data,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  }) as QResult<ProductsListResponse>;

  // Company-level risk history
  const riskHistoryQ = useQuery<CompanyRiskHistoryResponse>({
    queryKey: ["customer-risk-history", company, months, as_of_month],
    queryFn: async () =>
      (await getCompanyRiskHistory(company, months, as_of_month)).data,
    refetchOnWindowFocus: false,
  }) as QResult<CompanyRiskHistoryResponse>;

  // ---- Fallback product items when products endpoint returns empty ----
  const productItems = useMemo(() => {
    const apiItems = productsQ.data?.items ?? [];
    if (apiItems.length > 0) return apiItems;

    const fallback = summaryQ.data?.top_risk_products ?? [];
    return fallback.map((t) => ({
      product: t.product,
      risk_score_final: t.risk_score_final,
      risk_level: t.risk_level,
      // minimal shape to satisfy downstream components
      ur_cap: null,
      slope_4m: null,
      overuse_index: null,
      renewal_status: null,
      growth_opportunity_flag: null,
    }));
  }, [productsQ.data, summaryQ.data]);

  // Per-product trend queries
  const trendQueries = useQueries({
    queries: productItems.map((p) => ({
      queryKey: ["product-trend", company, p.product, months, as_of_month],
      queryFn: async () =>
        (await getProductTrend(company, p.product, months, as_of_month)).data,
      enabled: productItems.length > 0,
      refetchOnWindowFocus: false,
    })),
  }) as QResult<ProductTrendResponse>[];

  // Per-product risk trend queries
  const riskTrendQueries = useQueries({
    queries: productItems.map((p) => ({
      queryKey: ["product-risk-trend", company, p.product, months, as_of_month],
      queryFn: async () =>
        (
          await getProductRiskTrend(company, p.product, months, as_of_month)
        ).data,
      enabled: productItems.length > 0,
      refetchOnWindowFocus: false,
    })),
  }) as QResult<ProductRiskTrendResponse>[];

  // Per-product LLM analysis queries
  const llmQueries = useQueries({
    queries: productItems.map((p) => ({
      queryKey: ["product-llm", company, p.product, as_of_month],
      queryFn: async () =>
        (await getProductLLMAnalysis(company, p.product, as_of_month)).data,
      enabled: productItems.length > 0,
      refetchOnWindowFocus: false,
    })),
  }) as QResult<ProductLLMAnalysisResponse>[];

  // Merge all per-product pieces into a single array for UI
  const perProduct = useMemo(() => {
    const asMap = <T extends { company_name?: string; product?: string }>(
      arr: QResult<T>[]
    ) => {
      const m = new Map<string, T>();
      arr.forEach((q) => {
        if (!q.data) return;
        const key = `${q.data.company_name ?? company}::${q.data.product}`;
        m.set(key, q.data);
      });
      return m;
    };

    const trends = asMap(trendQueries);
    const riskTrends = asMap(riskTrendQueries);
    const llms = asMap(llmQueries);

    return productItems.map((p) => {
      const key = `${company}::${p.product}`;
      return {
        meta: p,
        trend: trends.get(key),
        riskTrend: riskTrends.get(key),
        llm: llms.get(key),
      };
    });
  }, [company, productItems, trendQueries, riskTrendQueries, llmQueries]);

  const loading =
    !!summaryQ.isLoading ||
    !!productsQ.isLoading ||
    !!riskHistoryQ.isLoading ||
    trendQueries.some((q) => q.isLoading) ||
    riskTrendQueries.some((q) => q.isLoading) ||
    llmQueries.some((q) => q.isLoading);

  const firstErr =
    summaryQ.error ||
    productsQ.error ||
    riskHistoryQ.error ||
    trendQueries.find((q) => q.error)?.error ||
    riskTrendQueries.find((q) => q.error)?.error ||
    llmQueries.find((q) => q.error)?.error ||
    null;

  return {
    summary: summaryQ.data,
    products: productsQ.data,
    riskHistory: riskHistoryQ.data,
    perProduct,
    loading,
    error: firstErr,
  };
}
